import { DateTime } from 'luxon';

/**
 * Formats a date string to a localized time string
 * @param dateString - ISO date string
 * @returns Formatted time string (e.g., "2:30 PM") or null if invalid
 */
export const formatTime = (dateString?: string): string | null => {
  if (!dateString) return null;

  try {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return null;
  }
};

/**
 * Formats start and end dates to a time range string
 * @param startDate - ISO start date string
 * @param endDate - ISO end date string (optional)
 * @returns Formatted time range (e.g., "2:30 PM - 4:00 PM") or single time or null
 */
export const formatTimeRange = (
  startDate?: string,
  endDate?: string,
): string | null => {
  const startTime = formatTime(startDate);
  if (!startTime) return null;

  const endTime = formatTime(endDate);
  if (endTime) {
    return `${startTime} - ${endTime}`;
  }

  return startTime;
};

/**
 * Formats a date string to 'Weekday Month Day' (e.g., 'Tuesday October 7')
 * @param dateString - ISO date string
 * @returns Formatted date string or null if invalid
 */
export const formatLongDate = (dateString?: string): string | null => {
  if (!dateString) return null;

  try {
    return DateTime.fromISO(dateString).toFormat('cccc MMMM d');
  } catch {
    return null;
  }
};

/**
 * Formats a date string to 'Weekday, Month Day, Year' (e.g., 'Thursday, October 10, 2025')
 * @param dateString - ISO date string
 * @returns Formatted date string or null if invalid
 */
export const formatLongDateWithYear = (dateString?: string): string | null => {
  if (!dateString) return null;

  try {
    return DateTime.fromISO(dateString).toFormat('cccc, MMMM d, yyyy');
  } catch {
    return null;
  }
};

/**
 * Formats a time range for same-day events.
 * Returns start time only, or start-end range if both are on the same day.
 *
 * @param startDateString - ISO start date string
 * @param endDateString - ISO end date string (optional)
 * @returns Formatted time range (e.g., "2:30 PM - 4:00 PM") or null if invalid
 */
export const formatSameDayTimeRange = (
  startDateString?: string,
  endDateString?: string,
): string | null => {
  if (!startDateString) return null;

  try {
    const startDate = DateTime.fromISO(startDateString);
    if (!startDate.isValid) return null;

    const startTime = startDate.toFormat('h:mm a');

    if (!endDateString) return startTime;

    const endDate = DateTime.fromISO(endDateString);
    if (!endDate.isValid) return startTime;

    // Only show range if same day
    if (startDate.hasSame(endDate, 'day')) {
      const endTime = endDate.toFormat('h:mm a');
      return `${startTime} - ${endTime}`;
    }

    return startTime;
  } catch {
    return null;
  }
};

/**
 * Formats a date range with intelligent condensing based on overlap.
 *
 * Examples:
 * - Same day: "Thursday, December 11, 2025"
 * - Same month/year: "December 6 - 10, 2025"
 * - Same year: "December 6 - January 10, 2025"
 * - Different years: "December 6, 2024 - January 10, 2025"
 *
 * @param startDateString - ISO start date string
 * @param endDateString - ISO end date string (optional, defaults to start date)
 * @returns Formatted date range string or null if invalid
 */
export const formatDateRangeCondensed = (
  startDateString?: string,
  endDateString?: string,
): string | null => {
  if (!startDateString) return null;

  try {
    const startDate = DateTime.fromISO(startDateString);
    if (!startDate.isValid) return null;

    const endDate = endDateString ? DateTime.fromISO(endDateString) : startDate;
    if (!endDate.isValid) return null;

    // Same day: "Thursday, December 11, 2025"
    if (startDate.hasSame(endDate, 'day')) {
      return startDate.toFormat('EEEE, LLLL d, yyyy');
    }

    // Same month and year: "December 6 - 10, 2025"
    if (
      startDate.hasSame(endDate, 'month') &&
      startDate.hasSame(endDate, 'year')
    ) {
      return `${startDate.toFormat('LLLL d')} - ${endDate.toFormat('d, yyyy')}`;
    }

    // Same year: "December 6 - January 10, 2025"
    if (startDate.hasSame(endDate, 'year')) {
      return `${startDate.toFormat('LLLL d')} - ${endDate.toFormat('LLLL d, yyyy')}`;
    }

    // Different years: "December 6, 2024 - January 10, 2025"
    return `${startDate.toFormat('LLLL d, yyyy')} - ${endDate.toFormat('LLLL d, yyyy')}`;
  } catch {
    return null;
  }
};
